import React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { COMPACT_HEADER_STYLE } from '../../lib/gameChrome'
import { goBackOr } from '../../lib/goBack'
import { useSupportUnread } from '../../hooks/useSupportInbox'
import { useTheme } from '../../hooks/useTheme'
import { KeyboardAwareScrollView } from '../ui/KeyboardAwareScrollView'
import { HubCard, type HubIconName } from './HubCard'

type HelpRoute =
  | '/(app)/settings/contact?reason=bug'
  | '/(app)/settings/contact?reason=feature'
  | '/(app)/settings/contact?reason=feedback'
  | '/(app)/settings/report-user'
  | '/(app)/settings/inbox'

const START_ACTIONS: ReadonlyArray<{
  icon: HubIconName
  title: string
  subtitle: string
  route: HelpRoute
  accessibilityLabel: string
}> = [
  {
    icon: 'bug-outline',
    title: 'Bug Report',
    subtitle: 'Something is broken or behaves wrong',
    route: '/(app)/settings/contact?reason=bug',
    accessibilityLabel: 'Bug Report',
  },
  {
    icon: 'lightbulb-on-outline',
    title: 'Feature Request',
    subtitle: 'An idea or improvement you want to see',
    route: '/(app)/settings/contact?reason=feature',
    accessibilityLabel: 'Feature Request',
  },
  {
    icon: 'chat-question-outline',
    title: 'General Feedback',
    subtitle: "Anything you'd like to share",
    route: '/(app)/settings/contact?reason=feedback',
    accessibilityLabel: 'General Feedback',
  },
  {
    icon: 'shield-alert-outline',
    title: 'Report User',
    subtitle: 'Tell the WarCarp team about a player',
    route: '/(app)/settings/report-user',
    accessibilityLabel: 'Report User',
  },
]

export function HelpSupportScreen() {
  const router = useRouter()
  const { theme } = useTheme()
  const unread = useSupportUnread().data?.count ?? 0
  const iconColor = theme === 'dark' ? '#e6e1c4' : '#18170f'

  return (
    <SafeAreaView className="flex-1 bg-canvas" edges={['top']}>
      <View className="flex-row items-center px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOr(router, '/(app)/settings')}
          accessibilityLabel="Back"
          accessibilityRole="button"
          className="mr-3 p-1"
        >
          <Text className="text-blood text-base font-typed">← Back</Text>
        </TouchableOpacity>
        <Text className="text-ink font-stencil text-xl flex-1 uppercase">Help & Support</Text>
      </View>

      <KeyboardAwareScrollView contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 32 }}>
        <HubCard
          icon="inbox-arrow-down-outline"
          iconColor={iconColor}
          title="Inbox"
          subtitle={unread > 0 ? 'You have unread messages from WarCarp' : 'News and replies from WarCarp'}
          accessibilityLabel="Inbox"
          unreadCount={unread}
          onPress={() => router.push('/(app)/settings/inbox')}
        />
        <View testID="help-support-divider" className="h-px bg-olive my-1" />
        {START_ACTIONS.map((action) => (
          <HubCard
            key={action.route}
            icon={action.icon}
            iconColor={iconColor}
            title={action.title}
            subtitle={action.subtitle}
            accessibilityLabel={action.accessibilityLabel}
            onPress={() => router.push(action.route)}
          />
        ))}
      </KeyboardAwareScrollView>
    </SafeAreaView>
  )
}
